// Levels in Penguins. The id is fixed (progress is saved under it) – never change it once a level is published.
import { defineLevels } from '../../core/registry';
import type { LevelData, StaticData } from '../../core/types';
import { on } from '../../core/level-helpers';
import { DEG } from './shared';

// ---------- helpers for the levels ----------
// Fixed blocks: ice (slippery) and rock (ground and walls)
const ICE = (x: number, y: number, w: number, a?: number, h?: number): StaticData => ({ x, y, w, h: h || 24, a: a || 0, skin: 'penguins_ice' });
const ROCK = (x: number, y: number, w: number, h: number, a?: number): StaticData => ({ x, y, w, h, a: a || 0, skin: 'penguins_rock' });
const P = 15;

// Mirror a level (start on the right instead of the left)
interface Mirrorable { x: number; a?: unknown; [extra: string]: unknown }
function mirror(l: LevelData): LevelData {
  const m = <T extends Mirrorable>(o: T): T => Object.assign({}, o, { x: 1200 - o.x }, o.a != null ? { a: -(o.a as number) } : {});
  const r = Object.assign({}, l);
  r.statics = l.statics!.map(m); r.balls = l.balls!.map(m); r.goal = m(l.goal!);
  // mirrored levels only have objects placed by x/y (duck, friend)
  if (l.objs) r.objs = l.objs.map(o => Object.assign(m(o as typeof o & Mirrorable), o.t === 'penguins_friend' ? { flip: !o.flip } : {}));
  if (l.fixed) r.fixed = l.fixed.map(m);
  r.solution = l.solution!.map(m); if (l.fails) r.fails = l.fails.map(f => f.map(m));
  return r;
}
// "Brake slope": ice slope, gap (the child's part), short ice lip, rock and bucket below
function brake(bx: number, extra: LevelData): LevelData {
  const s1 = ICE(230, 200, 340, 20), lip = ICE(650, 352, 180, 6);
  const base: LevelData = {
    statics: [s1, lip, ROCK(bx, 690, 200, 180)],
    balls: [on(s1, -140, P)],
    goal: { type: 'bowl', x: bx, y: 590, w: 170 },
  };
  return Object.assign(base, extra);
}
// Ice slope + long ice ledge (tilted by 'a') out to a rock; the bucket stands below. The child puts snow/gravel ON TOP of the ice.
function ledge(a: number, bx: number, by: number, extra: LevelData): LevelData {
  const s1 = ICE(250, 190, 380, 24), fl = ICE(630 + (a ? 0 : 10), a ? Math.round(278 + 210 * Math.sin(a * DEG)) : 288, 420, a);
  const base: LevelData = { statics: [s1, fl, ROCK(bx, by + 100, 200, 180)], balls: [on(s1, -160, P)], goal: { type: 'bowl', x: bx, y: by, w: 170 } };
  return Object.assign(base, extra);
}

const L: LevelData[] = [];
{ // 1 – Slide – intro: ice (the chick slides a long way on ice)
  const s1 = ICE(210, 250, 320, 12);
  L.push({
    id: 'slide', par: 1, tools: { penguins_ice: 1 }, learn: ['friction', 'materials'], intro: 'penguins_ice',
    statics: [s1, ICE(760, 400, 420, 0), ROCK(1030, 660, 220, 180), ROCK(1150, 500, 40, 300)],
    balls: [on(s1, -130, P)],
    goal: { type: 'bowl', x: 1040, y: 570, w: 170 },
    objs: [{ t: 'duck', x: 760, y: 340 }, { t: 'penguins_friend', x: 330, y: 742, h: 64 }],
    solution: [{ t: 'penguins_ice', x: 460, y: 340, a: 15 }],
  });
}
{ // 2 – Snow slows you down – intro: snow (without snow the chick flies over the bucket)
  L.push(brake(800, {
    id: 'snow-brakes', par: 1, tools: { penguins_snow: 1 }, learn: ['friction', 'materials'], intro: 'penguins_snow',
    objs: [{ t: 'duck', x: 960, y: 420 }, { t: 'penguins_friend', x: 470, y: 742, h: 64 }],
    solution: [{ t: 'penguins_snow', x: 520, y: 300, a: 15 }],
  }));
}
{ // 3 – Belly-slide penguin – intro: penguin (slides on its belly and pushes the chick)
  const s1 = ICE(250, 250, 340, 22), s2 = ICE(600, 318, 380, 0);
  L.push({
    id: 'belly-penguin', par: 1, tools: { penguins_penguin: 1 }, learn: ['friction', 'chain_reaction'], intro: 'penguins_penguin',
    statics: [s1, s2, ROCK(930, 650, 200, 200), ROCK(1100, 480, 40, 260)],
    balls: [on(s2, 40, P)],
    goal: { type: 'bowl', x: 940, y: 548, w: 170 },
    objs: [{ t: 'duck', x: 960, y: 300 }],
    solution: [{ t: 'penguins_penguin', x: 260, y: 170, a: 15 }],
  });
}
{ // 4 – Brake – ice or snow? Put snow on the ice so the chick doesn't fly past the bucket (mirrored)
  L.push(mirror(ledge(15, 900, 600, {
    id: 'brake', par: 1, tools: { penguins_ice: 1, penguins_snow: 1 }, learn: ['friction', 'materials'],
    objs: [{ t: 'duck', x: 1060, y: 470 }, { t: 'penguins_friend', x: 600, y: 742, h: 64 }],
    solution: [{ t: 'penguins_snow', x: 580, y: 300, a: 15 }], fails: [[{ t: 'penguins_ice', x: 580, y: 300, a: 15 }], [{ t: 'penguins_gravel', x: 580, y: 300, a: 15 }]],
  })));
}
{ // 5 – Far away – same kind of slope, but now the chick must go FAR: ice over the gap (mirrored)
  const s1 = ICE(210, 250, 320, 12);
  L.push(mirror({
    id: 'far-away', par: 1, tools: { penguins_ice: 1, penguins_snow: 1 }, learn: ['friction', 'materials'],
    statics: [s1, ICE(780, 400, 380, 0), ROCK(1040, 660, 200, 180)],
    balls: [on(s1, -130, P)],
    goal: { type: 'bowl', x: 1040, y: 570, w: 170 },
    objs: [{ t: 'duck', x: 700, y: 330 }],
    solution: [{ t: 'penguins_ice', x: 460, y: 340, a: 15 }], fails: [[{ t: 'penguins_snow', x: 460, y: 340, a: 15 }]],
  }));
}
{ // 6 – Gravel – intro: gravel. Flat ledge: snow stops completely, ice flies past, gravel brakes a little
  L.push(ledge(0, 930, 590, {
    id: 'gravel', par: 1, tools: { penguins_ice: 1, penguins_snow: 1, penguins_gravel: 1 }, learn: ['friction', 'materials'], intro: 'penguins_gravel',
    objs: [{ t: 'duck', x: 1060, y: 420 }],
    solution: [{ t: 'penguins_gravel', x: 640, y: 270, a: 0 }], fails: [[{ t: 'penguins_ice', x: 640, y: 270, a: 0 }], [{ t: 'penguins_snow', x: 640, y: 270, a: 0 }]],
  }));
}
{ // 7 – The penguin run – the penguin only slides on ice: build an ice run for it (snow is the decoy)
  const s1 = ICE(200, 230, 260, 25), s2 = ICE(640, 360, 300, 0);
  L.push({
    id: 'penguin-run', par: 1, tools: { penguins_ice: 1, penguins_snow: 1 }, learn: ['friction', 'chain_reaction'],
    statics: [s1, s2, ROCK(930, 650, 200, 200), ROCK(1080, 470, 40, 260)],
    fixed: [{ t: 'penguins_penguin', x: 130, y: 180, a: 25 }],
    balls: [on(s2, 60, P)],
    goal: { type: 'bowl', x: 930, y: 548, w: 170 },
    objs: [{ t: 'duck', x: 1000, y: 380 }],
    solution: [{ t: 'penguins_ice', x: 380, y: 300, a: 15 }], fails: [[{ t: 'penguins_snow', x: 380, y: 300, a: 15 }]],
  });
}
{ // 8 – Flat or sloping – same snow, different slope: on the flat the chick stops, on the slope it creeps down
  const s1 = ICE(220, 180, 340, 24);
  L.push({
    id: 'flat-or-slope', par: 1, tools: { penguins_snow: 1, penguins_ice: 1 }, learn: ['friction', 'ramp'],
    statics: [s1, ICE(530, 272, 260, 0), ICE(760, 312, 220, 20), ROCK(950, 700, 200, 180)],
    balls: [on(s1, -140, P)],
    goal: { type: 'bowl', x: 940, y: 600, w: 170 },
    objs: [{ t: 'duck', x: 1080, y: 470 }, { t: 'penguins_friend', x: 300, y: 742, h: 64 }],
    solution: [{ t: 'penguins_snow', x: 770, y: 300, a: 15 }],
    fails: [[{ t: 'penguins_snow', x: 480, y: 252, a: 0 }], [{ t: 'penguins_ice', x: 770, y: 300, a: 15 }]],
  });
}
{ // 9 – Bridge and brake – two steps: ice over the gap (speed), snow on the slope (brake)
  const s1 = ICE(200, 170, 300, 22);
  L.push({
    id: 'bridge-and-brake', par: 2, tools: { penguins_ice: 1, penguins_snow: 1, penguins_gravel: 1 }, learn: ['friction', 'materials'],
    statics: [s1, ICE(640, 330, 240, 0), ICE(850, 368, 200, 20), ROCK(1040, 720, 200, 140)],
    balls: [on(s1, -120, P)],
    goal: { type: 'bowl', x: 1030, y: 640, w: 170 }, sea: 760,
    objs: [{ t: 'duck', x: 640, y: 230 }],
    solution: [{ t: 'penguins_ice', x: 430, y: 280, a: 30 }, { t: 'penguins_snow', x: 850, y: 356, a: 15 }],
    fails: [[{ t: 'penguins_snow', x: 430, y: 280, a: 30 }, { t: 'penguins_ice', x: 850, y: 356, a: 15 }], [{ t: 'penguins_ice', x: 430, y: 280, a: 30 }]],
  });
}
{ // 10 – Three chicks – counting: all three chicks must be braked into the bucket (mirrored)
  const s1 = ICE(220, 180, 340, 24);
  L.push(mirror({
    id: 'three-chicks', par: 1, tools: { penguins_ice: 1, penguins_snow: 1, penguins_gravel: 1 }, learn: ['friction', 'counting'],
    statics: [s1, ICE(530, 272, 260, 0), ICE(760, 312, 220, 20), ROCK(950, 700, 200, 180)],
    balls: [on(s1, -150, P), on(s1, -110, P), on(s1, -70, P)],
    goal: { type: 'bowl', x: 940, y: 600, w: 170, count: 3 },
    objs: [{ t: 'duck', x: 530, y: 200 }],
    solution: [{ t: 'penguins_snow', x: 770, y: 300, a: 15 }],
    fails: [[{ t: 'penguins_gravel', x: 770, y: 300, a: 15 }], [{ t: 'penguins_ice', x: 770, y: 300, a: 15 }]],
  }));
}
{ // 11 – The penguin's path – place the penguin yourself and build its ice run (mirrored)
  const s1 = ICE(200, 230, 260, 25), s2 = ICE(640, 360, 300, 0);
  L.push(mirror({
    id: 'penguin-path', par: 2, tools: { penguins_penguin: 1, penguins_ice: 1, penguins_snow: 1 }, learn: ['friction', 'chain_reaction'],
    statics: [s1, s2, ROCK(930, 650, 200, 200), ROCK(1080, 470, 40, 260)],
    balls: [on(s2, 60, P)],
    goal: { type: 'bowl', x: 930, y: 548, w: 170 },
    objs: [{ t: 'duck', x: 250, y: 120 }],
    solution: [{ t: 'penguins_penguin', x: 130, y: 170, a: 30 }, { t: 'penguins_ice', x: 380, y: 300, a: 15 }],
    fails: [[{ t: 'penguins_penguin', x: 130, y: 170, a: 30 }, { t: 'penguins_snow', x: 380, y: 300, a: 15 }], [{ t: 'penguins_ice', x: 380, y: 300, a: 15 }]],
  }));
}
{ // 12 – A long trip – ice over the gap, gravel on the flat ledge so the chick lands in the bucket
  const s1 = ICE(200, 170, 300, 22);
  L.push({
    id: 'long-trip', par: 2, tools: { penguins_ice: 1, penguins_snow: 1, penguins_gravel: 1 }, learn: ['friction', 'materials'],
    statics: [s1, ICE(700, 330, 420, 0), ROCK(990, 730, 200, 140)],
    balls: [on(s1, -120, P)],
    goal: { type: 'bowl', x: 990, y: 630, w: 170 }, sea: 760,
    objs: [{ t: 'duck', x: 420, y: 200 }, { t: 'penguins_friend', x: 260, y: 762, h: 64 }],
    solution: [{ t: 'penguins_ice', x: 420, y: 280, a: 30 }, { t: 'penguins_gravel', x: 720, y: 312, a: 0 }],
    fails: [[{ t: 'penguins_ice', x: 420, y: 280, a: 30 }, { t: 'penguins_snow', x: 720, y: 312, a: 0 }], [{ t: 'penguins_ice', x: 420, y: 280, a: 30 }]],
  });
}
{ // 13 – Skiing – the ski (plank) is slippery too! Ski over the gap, snow to brake (mirrored)
  const s1 = ICE(200, 170, 300, 22);
  L.push(mirror({
    id: 'skiing', par: 2, tools: { plank: 1, penguins_snow: 1, penguins_gravel: 1 }, learn: ['friction', 'materials', 'ramp'],
    statics: [s1, ICE(640, 330, 240, 0), ICE(850, 368, 200, 20), ROCK(1040, 720, 200, 140)],
    balls: [on(s1, -120, P)],
    goal: { type: 'bowl', x: 1030, y: 640, w: 170 }, sea: 760,
    objs: [{ t: 'duck', x: 900, y: 250 }],
    solution: [{ t: 'plank', x: 430, y: 280, a: 30 }, { t: 'penguins_snow', x: 850, y: 356, a: 15 }],
    fails: [[{ t: 'plank', x: 430, y: 280, a: 30 }], [{ t: 'penguins_snow', x: 430, y: 280, a: 30 }, { t: 'penguins_gravel', x: 850, y: 356, a: 15 }]],
  }));
}
{ // 14 – The seal – intro: pad (the seal bounces the chick up, snow brakes it on the ice)
  const s1 = ICE(150, 230, 200, 5), H = ICE(720, 430, 380, 15);
  L.push({
    id: 'seal', par: 2, tools: { pad: 1, penguins_snow: 1, penguins_ice: 1 }, learn: ['friction', 'spring'], intro: 'pad',
    statics: [s1, H, ROCK(430, 560, 44, 440), ROCK(1010, 740, 200, 120)],
    balls: [on(s1, -60, P)],
    goal: { type: 'bowl', x: 1010, y: 670, w: 170 }, sea: 770,
    objs: [{ t: 'duck', x: 560, y: 250 }],
    solution: [{ t: 'pad', x: 330, y: 440, a: 30 }, { t: 'penguins_snow', x: 760, y: 427, a: 15 }],
    fails: [[{ t: 'pad', x: 330, y: 440, a: 30 }], [{ t: 'pad', x: 330, y: 440, a: 30 }, { t: 'penguins_ice', x: 760, y: 427, a: 15 }]],
  });
}
{ // 15 – Master penguin – the penguin pushes, a slippery bridge and a brake at the end (several solutions)
  const P0 = ICE(360, 200, 260, 0);
  L.push({
    id: 'master-penguin', par: 3, tools: { penguins_penguin: 1, penguins_ice: 1, plank: 1, penguins_snow: 1, penguins_gravel: 1 }, learn: ['friction', 'materials', 'chain_reaction'],
    statics: [ICE(130, 140, 200, 25), P0, ICE(740, 320, 160, 0), ICE(920, 360, 200, 20), ROCK(1080, 735, 190, 130)],
    balls: [on(P0, -50, P)],
    goal: { type: 'bowl', x: 1080, y: 670, w: 160 }, sea: 770,
    objs: [{ t: 'duck', x: 600, y: 460 }, { t: 'penguins_friend', x: 300, y: 762, h: 64 }],
    solution: [{ t: 'penguins_penguin', x: 110, y: 100, a: 30 }, { t: 'penguins_ice', x: 575, y: 255, a: 30 }, { t: 'penguins_snow', x: 920, y: 346, a: 15 }],
    fails: [[{ t: 'penguins_penguin', x: 110, y: 100, a: 30 }, { t: 'penguins_ice', x: 575, y: 255, a: 30 }], [{ t: 'penguins_ice', x: 575, y: 255, a: 30 }, { t: 'penguins_snow', x: 920, y: 346, a: 15 }]],
  });
}

export const levels = defineLevels(L);
